
import React, { useContext, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { NavBarCollapsableContext } from '../../../helpers/context'
import QrList from '../../../components/qrTable'
import { useNavigate } from 'react-router-dom'
import DialogModal from '../../../components/dialogModal'
import EditQrForm from '../../../components/forms/qrCodes/formEdit'
import { useWindowSize } from '../../../helpers/hooks/hookWindowsSize'
import QRCodeDownload from '../../../components/qrDownload'
import { getToken, getTokenExpiration, logOut } from '../../../helpers/utils'
import { AppDispatch, useAppDispatch } from '../../../redux/store'
import {
  deleteLink,
  editLink,
  getLinks,
  getTotalLinks,
} from '../../../redux/reducers/urlReducer'
import { toast } from 'react-toastify'
import DeleteLinkForm from '../../../components/forms/links/formDelete'

interface QRCodeValues {
  _id: string
  original_url: string
  short_url: string
  title: string
  domain: string
  custom_code: string
  has_qr: boolean
  status: number
  tracking_count: number
  createdAt: string
  qr_code: {
    color: string
    logo: string
  }
}

interface EditFormValues {
  _id: string
  title: string
  qr_code: {
    color: string
    logo: string
  }
  short_url: string
  custom_code: string
  original_url: string
  domain: string
}

interface DeleteFormValues {
  _id: string
}

const QrCodes = () => {
  const initialQrSelected: QRCodeValues = {
    _id: '',
    original_url: '',
    short_url: '',
    title: '',
    domain: '',
    custom_code: '',
    has_qr: true,
    qr_code: {
      color: '',
      logo: '',
    },
    status: 1,
    createdAt: '',
    tracking_count: 0,
  }

  const { t } = useTranslation()
  const dispatch: AppDispatch = useAppDispatch()
  const classContext = useContext(NavBarCollapsableContext)
  const navigate = useNavigate()
  const [qrSelected, setQrSelected] = useState<QRCodeValues>(initialQrSelected)
  const [openDownload, setOpenDownload] = useState<boolean>(false)
  const [openEdit, setOpenEdit] = useState<boolean>(false)
  const [openDelete, setOpenDelete] = useState<boolean>(false)
  const sizeWidth = useWindowSize().width
  const [page, setPage] = useState(1)
  const [qrData, setQrData] = useState<QRCodeValues[]>([])
  const [totalCountData, setTotalCountData] = useState<number>(0)
  const [totalPagesData, setTotalPagesData] = useState<number>(0)
  const currentDate = new Date()
  const firstDayOfMonth = new Date(
    currentDate.getFullYear(),
    currentDate.getMonth(),
    1
  )
  const formattedFirstDayOfMonth = `${firstDayOfMonth.getFullYear()}-${(
    firstDayOfMonth.getMonth() + 1
  )
    .toString()
    .padStart(2, '0')}-01`
  const formattedDate = `${currentDate.getFullYear()}-${(
    currentDate.getMonth() + 1
  )
    .toString()
    .padStart(2, '0')}-${currentDate.getDate().toString().padStart(2, '0')}`

  const [fromDateSelected, setFromDateSelected] = useState<string>(
    formattedFirstDayOfMonth
  )

  const [toDateSelected, setToDateSelected] = useState<string>(formattedDate)
  const token = getToken()
  const sessionExpired = getTokenExpiration()

  useEffect(() => {
    if (sessionExpired) logOut()
  }, [sessionExpired])

  useEffect(() => {
    getData(page)
    getTotalData()
  }, [fromDateSelected, toDateSelected])

  const getData = async (page: number) => {
    try {
      const { payload } = await dispatch(
        getLinks({
          token: token,
          page: page,
          hasqr: 1,
          status: 1,
          fromDate: '',
          toDate: '',
          tag: [],
        })
      )

      if (payload?.success) {
        setQrData(payload?.data)
      } else {
        console.error(`${t('ERROR')}`)
      }
    } catch (error) {
      console.error('Error:', error)
    }
  }

  const getTotalData = async () => {
    try {
      const { payload } = await dispatch(
        getTotalLinks({
          token: token,
          hasqr: 1,
          status: 1,
          fromDate: fromDateSelected,
          toDate: toDateSelected,
          tag: [],
        })
      )

      if (payload?.success) {
        setTotalCountData(payload?.data?.total)
        setTotalPagesData(payload?.data?.pages)
      } else {
        console.error(`${t('ERROR')}`)
      }
    } catch (error) {
      console.error('Error:', error)
    }
  }

  const handleEditQr = async (body: any) => {
    try {
      const { payload } = await dispatch(editLink({ token, body: body }))

      if (payload && payload.success) {
        toast(`${t('DATA_UPDATED')}`)
        getData(page)
      } else {
        toast(`${t('ERROR')}`)
      }
    } catch (error) {
      console.error('Error:', error)
    }
  }

  const handleChangePage = (newPage: number) => {
    setPage(newPage)
    getData(newPage)
  }
  const handleSubmitEdit = (values: EditFormValues) => {
    const body = {
      urlId: values._id,
      url: values.original_url,
      domain: values.domain,
      title: values.title,
      custom_code: values.custom_code,
      has_qr: true,
      qr_code: { color: values.qr_code.color, logo: values.qr_code.logo },
      utm: null,
    }

    handleEditQr(body)
    setOpenEdit(false)
  }

  const handleDeleteQr = async (id: string) => {
    try {
      const { payload } = await dispatch(deleteLink({ token, urlId: id }))

      if (payload && payload.success) {
        toast(`${t('DATA_DELETED')}`)
        getData(page)
        getTotalData()
      } else {
        toast(`${t('ERROR')}`)
      }
    } catch (error) {
      console.error('Error:', error)
    }
  }

  const handleSubmitDelete = (values: DeleteFormValues) => {
    handleDeleteQr(values._id)
    setOpenDelete(false)
  }

  const handleCloseEdit = () => {
    setOpenEdit(false)
  }

  const getQrData = (linkCode: QRCodeValues) => {
    setQrSelected(linkCode)
  }

  const handleCloseDelete = () => {
    setOpenDelete(false)
  }

  return (
    <>
      <div className={`container-home`}>
        <div className={classContext.classes.navbar_padding_left}>
          <div className='container-table'>
            <div className='container-title-table'>
              <span className='principal-title'>{`${t('QR_CODES')}`}</span>
              <button
                className={`button-second ${
                  sizeWidth < 768 ? 'margin-top-2 width-100' : 'width-auto'
                }`}
                type='button'
                onClick={() => navigate('/create-new-qr-code')}
              >
                {`${t('CREATE_NEW')}`}
              </button>
            </div>

            <QrList
              dataBody={qrData}
              dataQrCodeSelected={getQrData}
              setOpenDownload={setOpenDownload}
              setOpenEdit={setOpenEdit}
              setOpenDelete={setOpenDelete}
              totalRecords={totalCountData}
              totalPages={totalPagesData}
              onCounterChange={handleChangePage}
              onPageChange={setPage}
            />
          </div>
        </div>

        <DialogModal
          title={`${t('DOWNLOAD_YOUR_QR_CODE')}`}
          open={openDownload}
          handleClose={() => setOpenDownload(false)}
        >
          <QRCodeDownload qrData={qrSelected} />
        </DialogModal>

        <DialogModal
          title={`${t('EDIT_QR_CODE')}`}
          open={openEdit}
          handleClose={() => setOpenEdit(false)}
        >
          <div className='d-flex flex-column'>
            <EditQrForm
              data={qrSelected}
              onSubmit={handleSubmitEdit}
              handleClose={handleCloseEdit}
            />
          </div>
        </DialogModal>

        <DialogModal
          title={`${t('DELETE_LINK')}`}
          open={openDelete}
          handleClose={() => setOpenDelete(false)}
        >
          <DeleteLinkForm
            data={qrSelected}
            onSubmit={handleSubmitDelete}
            handleClose={handleCloseDelete}
          />
        </DialogModal>
      </div>
    </>
  )
}

export default QrCodes
